<script setup>
import { ref, onMounted, computed, watch, onUnmounted } from "vue";
import { useModals } from "@/utils/useModals";
import { useFieldsStore } from "@/stores/storeFieldData";
import { useRequestsStore } from "@/stores/storeRequests";
import { useSettingsStore } from "@/stores/storeSettings";
import { policyUrl, rulesUrl, agreementUrl } from "@/utils/provider";
import { useRoute, RouterLink, useRouter } from "vue-router";
import { sendMetrik } from "@/utils/metrika";
import BaseInput from "@/components/ui/BaseInput.vue";
import BasePhoneInput from "@/components/ui/BasePhoneInput.vue";
import BaseCheckbox from "@/components/ui/BaseCheckbox.vue";
import BaseSelect from "@/components/ui/BaseSelect.vue";
import BaseTextarea from "@/components/ui/BaseTextarea.vue";
import BaseFile from "@/components/ui/BaseFile.vue";
import BasePassword from "@/components/ui/BasePassword.vue";
import BasePasswordRepeat from "@/components/ui/BasePasswordRepeat.vue";

const storeField = useFieldsStore();
const storeSettings = useSettingsStore();
const storeRequests = useRequestsStore();
const route = useRoute();

const selectOptions = ref([]);
const isPrizeId = ref({});
const pointerEventsNone = ref(false);
const email = ref("");
const name = ref("");
const idPrize = ref(null);

const password = defineModel();

const { activeModal, modalId, modalObj, closeModal, openModal } = useModals();

const userReceiptsTitle = {
	upload_available: "Загрузка чека доступна",
	upload_unavailable: "Загрузка чека недоступна, акция неактивна",
	maximum_receipt: "Ты уже загрузили максимальное количество чеков за период акции — 20 штук.",
	maximum_receipt_in_current_period: "Ты достиг максимального количества загрузок чеков за один день — 3 штуки. Новые загрузки будут доступны завтра.",
	maximum_upload_receipt: "У тебя есть чеки, ожидающие проверки. Чтобы не превысить лимит, новые чеки можно будет загрузить после завершения модерации.",
	maximum_upload_receipt_in_current_period: "У тебя есть чеки, ожидающие проверки. Чтобы не превысить лимит, новые чеки можно будет загрузить после завершения модерации.",
};

const setPrizeId = async (id) => {
	isPrizeId.value[id] = !isPrizeId.value[id];
	idPrize.value = id; 	
	console.log("🚀 ~ setPrizeId ~ idPrize.value:", idPrize.value)
	pointerEventsNone.value = !pointerEventsNone.value;
};

const userReceiptsStatus = (status) => {
	return userReceiptsTitle[status];
};

watch(
	() => storeRequests.isAuthenticated,
	(newVal) => {
		if (newVal) {
			storeField.updateFields("firstName", "disabledBtn", true);
			storeField.updateFields("email", "disabledBtn", true);
			storeField.updateFields("lastName", "disabledBtn", true);
			storeField.updateFields("phone", "disabledBtn", true);
		}
	}
);

watch(
	() => storeRequests.userData,
	(val) => {
		if (val?.email) email.value = val.email;
		if (val?.first_name) name.value = val.first_name;
	},
	{ immediate: true }
);

onMounted(async () => {
	selectOptions.value = await storeRequests.topics();

});

onUnmounted(() => {
	pointerEventsNone.value = false;
});
</script>
<template>
	<div style="position: absolute; bottom: 100%; overflow: hidden">
		<div id="apm-widget"></div>
	</div>
	<div :class="['modal', { 'is-open': activeModal }]" @click.self="closeModal">
		<Transition>
			<div v-if="activeModal" :class="['modal__container', { '--big': modalId === 'modalPrize' }]">
				<button class="modal-close" @click="closeModal">
					<svg width="40" height="40"><use href="#x"></use></svg>
				</button>
				<!-- Обратная связь -->
				<div class="modal-content" v-if="modalId === 'feedback'">
					<h5 class="modal-title">Обратная связь</h5>
					<form class="form" @submit.prevent="storeRequests.feedback()">
						<div class="form__block-inputns">
							<div class="form__wrap">
								<BaseInput type="text" placeholder="Имя" :required="true" state-key="firstName" v-model="name"> Имя </BaseInput>
							</div>
							<div class="form__wrap">
								<BaseInput type="email" placeholder="Email" :required="true" state-key="email" v-model="email">Email</BaseInput>
							</div>
							<div class="form__wrap">
								<BaseSelect :select-options></BaseSelect>
							</div>
							<div class="form__wrap">
								<BaseTextarea :max-length="500" state-key="message" placeholder="Сообщение">Сообщение</BaseTextarea>

								<BaseFile />
							</div>
						</div>
						<div class="form__footer">
							<BaseCheckbox :required="true" state-key="agreement">Согласен(на) с обработкой <a :href="agreementUrl" target="_blank" rel="noopener noreferrer">персональных данных</a></BaseCheckbox>

							<button :disabled="storeField.isBtnDisabled(['email', 'firstName', 'select', 'message', 'agreement'])" :class="['send-button form__btn btn --blue', { 'not-hover': storeRequests.loaderBtn }]">
								<span v-if="!storeRequests.loaderBtn">Отправить</span>
								<img v-else class="form__loader" width="36" height="36" src="@/assets/img/icons/loader.svg" alt="изображение" />
							</button>
							<p class="form__text-error" v-if="storeRequests.responseError">{{ storeRequests.responseError }}</p>
						</div>
					</form>
				</div>

				<!-- Авторизация -->
				<div class="modal-content" v-if="modalId === 'authorization'">
					<h5 class="modal-title">Авторизация</h5>
					<form class="form form-grid" @submit.prevent="storeRequests.authorization()">
						<div class="form__block-inputns">
							<div class="form__wrap">
								<BaseInput type="email" placeholder="Email" :required="true" state-key="email"> Логин/Email </BaseInput>
							</div>
							<div class="form__wrap">
								<BasePassword placeholder="Пароль (не менее 6 символов)" :required="true" state-key="password"> Пароль </BasePassword>
								<div class="form__text-btn-inner">
									<svg width="20" height="20"><use href="#icons_forgot_pass"></use></svg>
									<button @click="openModal('recoveryPassword')" class="form__text-btn" type="button">Забыли пароль?</button>
								</div>
							</div>
						</div>
						<div class="form__footer">
							<button :disabled="storeField.isBtnDisabled(['email', 'password'])" :class="['send-button form__btn btn --blue', { 'not-hover': storeRequests.loaderBtn }]">
								<span v-if="!storeRequests.loaderBtn">Войти</span>
								<img v-else class="form__loader" width="36" height="36" src="@/assets/img/icons/loader.svg" alt="изображение" />
							</button>
							<p class="form__text-error" v-if="storeRequests.responseError">{{ storeRequests.responseError }}</p>
							<div class="form__btn-wrapper" v-if="!storeSettings.statusStockPast">
								<span> Нет аккаунта? </span>
								<button @click="openModal('registration'), sendMetrik('reachGoal', 'registration_open')" class="send-button btn form__btn --blue">Регистрация</button>
							</div>
						</div>
					</form>
				</div>

				<!-- Требуется авторизация -->
				<div class="modal-content" v-if="modalId === 'authorizationRequired'">
					<h5 class="modal-title">Требуется авторизация</h5>
					<form class="form form-grid">
						<div class="form__footer">
							<div class="form__btn-wrapper">
								<span>У меня уже есть аккаунт</span>
								<button @click="openModal('authorization')" class="send-button btn form__btn --blue" type="button">Войти</button>
							</div>
							<div class="form__btn-wrapper" v-if="!storeSettings.statusStockPast">
								<span>У меня нет аккаунта</span>
								<button @click="openModal('registration'), sendMetrik('reachGoal', 'registration_open')" class="send-button btn form__btn --blue" type="button">Регистрация</button>
							</div>
						</div>
					</form>
				</div>

				<!-- Регистрация -->
				<div class="modal-content" v-if="modalId === 'registration'">
					<h5 class="modal-title">Регистрация</h5>
					<form class="form form-grid" @submit.prevent="storeRequests.registration()">
						<div class="form__block-inputns">
							<div class="form__wrap">
								<BaseInput type="text" placeholder="Имя" :required="true" state-key="firstName"> Имя</BaseInput>
							</div>
							<div class="form__wrap">
								<BaseInput type="text" placeholder="Фамилия" :required="true" state-key="lastName">Фамилия</BaseInput>
							</div>
							<div class="form__wrap">
								<BasePhoneInput :required="true" placeholder="Номер телефона" state-key="phone">Номер телефона </BasePhoneInput>
							</div>
							<div class="form__wrap">
								<BaseInput type="email" :required="true" placeholder="Email" state-key="email">Email </BaseInput>
							</div>
							<div class="form__wrap">
								<BasePassword placeholder="Пароль (не менее 6 символов)" :required="true" state-key="password" v-model="password"> Пароль </BasePassword>
							</div>
							<div class="form__wrap">
								<BasePasswordRepeat placeholder="Повторите пароль" :required="true" state-key="passwordRepeat" :original-password="computed(() => password)"> Повторите пароль </BasePasswordRepeat>
							</div>
						</div>
						<div class="form__checkboxes">
							<BaseCheckbox :required="true" state-key="rules">Я согласен(на) с <a :href="rulesUrl" target="_blank" rel="noopener noreferrer">правилами акции</a> </BaseCheckbox>
							<BaseCheckbox :required="true" state-key="agreement">Я согласен(на) на обработку моих <a :href="policyUrl" target="_blank" rel="noopener noreferrer">персональных данных</a> </BaseCheckbox>
							<BaseCheckbox :required="false" state-key="agreeToAdvertising">Я согласен(на) на получение персональных предложений от партнера</BaseCheckbox>
						</div>
						<p class="form__text-error" v-if="storeRequests.responseError">{{ storeRequests.responseError }}</p>
						<div class="form__footer">
							<button :disabled="storeField.isBtnDisabled(['firstName', 'lastName', 'phone', 'email', 'rules', 'agreement', 'passwordRepeat'])" :class="['send-button form__btn btn --blue', { 'not-hover': storeRequests.loaderBtn }]">
								<span v-if="!storeRequests.loaderBtn">Регистрация</span>
								<img v-else class="form__loader" width="36" height="36" src="@/assets/img/icons/loader.svg" alt="изображение" />
							</button>
							<div class="form__btn-wrapper">
								<span> Уже есть аккаунт? </span>
								<button @click="openModal('authorization')" class="send-button form__btn btn --border-grey">Войти</button>
							</div>
						</div>
					</form>
				</div>

				<!-- Восстановление пароля -->
				<div class="modal-content" v-if="modalId === 'recoveryPassword'">
					<h5 class="modal-title">Восстановление пароля</h5>
					<form class="form form-grid" @submit.prevent="storeRequests.recoveryPassword()">
						<div class="form__block-inputns">
							<div class="form__wrap">
								<BaseInput type="email" :required="true" placeholder="Email" state-key="email">Email </BaseInput>
							</div>
						</div>
						<div class="form__footer">
							<p class="form__text-error" v-if="storeRequests.responseError">{{ storeRequests.responseError }}</p>
							<button :disabled="storeField.isBtnDisabled(['email'])" :class="['send-button form__btn btn --blue', { 'not-hover': storeRequests.loaderBtn }]">
								<span v-if="!storeRequests.loaderBtn">Отправить</span>
								<img v-else class="form__loader" width="36" height="36" src="@/assets/img/icons/loader.svg" alt="изображение" />
							</button>
						</div>
					</form>
				</div>

				<!-- Изменение пароля -->
				<div class="modal-content" v-if="modalId === 'changeUserPassword'">
					<h5 class="modal-title">Изменение пароля</h5>
					<form class="form form-grid" @submit.prevent="storeRequests.changeUserPassword()">
						<div class="form__block-inputns">
							<div class="form__wrap">
								<BasePassword placeholder="Пароль (не менее 6 символов)" :required="true" state-key="password" v-model="password"> Новый пароль </BasePassword>
							</div>
							<div class="form__wrap">
								<BasePasswordRepeat placeholder="Повторите пароль" :required="true" state-key="passwordRepeat" :original-password="computed(() => password)"> Повторите новый пароль </BasePasswordRepeat>
							</div>
						</div>
						<div class="form__footer">
							<button :disabled="storeField.isBtnDisabled(['password', 'passwordRepeat'])" :class="['send-button form__btn btn --blue', { 'not-hover': storeRequests.loaderBtn }]">
								<span v-if="!storeRequests.loaderBtn">Сохранить</span>
								<img v-else class="form__loader" width="36" height="36" src="@/assets/img/icons/loader.svg" alt="изображение" />
							</button>
						</div>
					</form>
				</div>

				<!-- Изменение данных -->
				<div class="modal-content" v-if="modalId === 'changeUserProfile'">
					<h5 class="modal-title">Изменение данных</h5>
					<form class="form form-grid" @submit.prevent="storeRequests.changeUserProfile()">
						<div class="form__block-inputns">
							<div class="form__wrap">
								<BaseInput type="text" placeholder="Имя" :required="true" state-key="firstName" v-model="storeRequests.userData.first_name"> Имя</BaseInput>
							</div>
							<div class="form__wrap">
								<BaseInput type="text" placeholder="Фамилия" :required="true" state-key="lastName" v-model="storeRequests.userData.last_name">Фамилия</BaseInput>
							</div>
							<div class="form__wrap">
								<BasePhoneInput :required="true" placeholder="Номер телефона" state-key="phone">Номер телефона </BasePhoneInput>
							</div>
						</div>
						<p class="form__text-error" v-if="storeRequests.responseError">{{ storeRequests.responseError }}</p>
						<div class="form__footer --small-gap">
							<button :disabled="storeField.isBtnDisabled(['firstName', 'lastName', 'phone'])" :class="['send-button form__btn btn --blue', { 'not-hover': storeRequests.loaderBtn }]">
								<span v-if="!storeRequests.loaderBtn">Сохранить</span>
								<img v-else class="form__loader" width="36" height="36" src="@/assets/img/icons/loader.svg" alt="изображение" />
							</button>
							<button type="button" @click="openModal('authorization')" class="send-button form__btn btn --border-grey">Отмена</button>
						</div>
					</form>
				</div>

				<!-- Регистрация завершена -->
				<div class="modal-content" v-if="modalId === 'registrationEnd'">
					<h5 class="modal-title">Регистрация завершена</h5>
					<p class="modal-subtitle">На указанный при регистрации e-mail отправлено письмо.</p>
					<p class="modal-subtitle">Пожалуйста, перейдите по ссылке в письме, чтобы активировать ваш аккаунт и ознакомиться с паролем.</p>
					<p class="modal-subtitle">Не нашли письмо? Проверьте папку «Спам» или запросите повторное письмо</p>
					<div class="form form-grid">
						<button class="send-button btn form__btn --blue" type="button" @click="storeRequests.repeatVerification()">Запросить повторно</button>
					</div>
				</div>

				<!-- Пройти верефикацию -->
				<div class="modal-content" v-if="modalId === 'notVerified'">
					<h5 class="modal-title">Ваш аккаунт ещё не активирован</h5>
					<p class="modal-subtitle">Пожалуйста, перейдите по ссылке из письма, отправленного на ваш e-mail, чтобы завершить регистрацию.</p>
					<p class="modal-subtitle">Не нашли письмо? Проверьте папку «Спам» или запросите повторное письмо</p>
					<div class="form form-grid">
						<button class="send-button btn form__btn --blue" type="button" @click="storeRequests.repeatVerification()">Запросить повторно</button>
					</div>
				</div>

				<!-- Пройти верефикацию -->
				<div class="modal-content" v-if="modalId === 'errorVerified'">
					<h5 class="modal-title">Ошибка верификации</h5>
					<p class="modal-subtitle">Срок действия вашей ссылки для подтверждения истёк или ссылка недействительна. Пожалуйста, запросите новую ссылку.</p>
					<div class="form form-grid">
						<div class="form__wrap">
							<BaseInput type="email" :required="true" placeholder="Email" state-key="email">Email </BaseInput>
						</div>
						<button :disabled="storeField.isBtnDisabled(['email'])" class="send-button btn form__btn --blue" type="button" @click="storeRequests.repeatVerification()">Запросить повторно</button>
					</div>
				</div>

				<!-- Пройти верефикацию повторное письмо -->
				<div class="modal-content" v-if="modalId === 'endRepeatVerified'">
					<h5 class="modal-title">На ваш e-mail отправлено письмо с данными для входа</h5>
					<p class="modal-subtitle">Пожалуйста, перейдите по ссылке в письме, чтобы активировать ваш аккаунт и ознакомиться с паролем. Не нашли письмо? Проверьте папку «Спам» или запросите повторное письмо</p>
				</div>

				<!-- Восстановление пароля send Email -->
				<div class="modal-content" v-if="modalId === 'sentNewPassword'">
					<h5 class="modal-title">Запрос на восстановление пароля принят</h5>
					<p class="modal-subtitle">На ваш e-mail отправлено письмо с паролем для входа в личный кабинет.</p>
					<p class="modal-subtitle">Если письмо не пришло в течение нескольких минут, проверьте папку «Спам» или запросите повторное.</p>
					<div class="form form-grid">
						<button class="send-button btn form__btn --blue" @click="openModal('authorization')">Войти</button>
					</div>
				</div>

				<!-- Изменения пароля send Email -->
				<div class="modal-content" v-if="modalId === 'sentChangeNewPassword'">
					<h5 class="modal-title --center">Изменение пароля</h5>
					<p class="modal-subtitle --center">Ваш пароль изменен!</p>
				</div>

				<!-- Изменения данных -->
				<div class="modal-content" v-if="modalId === 'sentChangeUserProfile'">
					<h5 class="modal-title --center">Изменение данных</h5>
					<p class="modal-subtitle --center">Ваши данные изменены</p>
				</div>

				<!-- Ответ на feedback -->
				<div class="modal-content" v-if="modalId === 'statusOk'">
					<h5 class="modal-title">Спасибо за обращение!</h5>
					<p class="modal-subtitle">Мы обязательно Вам ответим в течение 3 рабочих дней.</p>
				</div>

				<!-- Акция еще не началась-->
				<div class="modal-content" v-if="modalId === 'promoNotStart'">
					<h5 class="modal-title --center" style="text-align: center">Акция скоро начнётся!</h5>
					<p class="modal-subtitle --center">Уже совсем скоро сможешь загружать чеки и выигрывать призы. Следи за обновлениями!</p>
				</div>

				<!-- Акция закончилась -->
				<div class="modal-content" v-if="modalId === 'promoEnd'">
					<h5 class="modal-title --center" style="text-align: center">Акция завершилась!</h5>
					<p class="modal-subtitle --center">Список победителей ты можешь посмотреть на сайте.</p>
				</div>

				<!-- Призы в профиле modalObj.prize.length -->
				<div class="modal-content" v-if="modalId === 'modalPrize'">					
					<div class="modal-prizes" v-if="modalObj.prize.length">
						<button :class="['modal-prizes__btn', { active: isPrizeId[prize.id], 'pointer-events-none': pointerEventsNone }]" v-for="prize in modalObj.prize" :key="prize.id" @click="setPrizeId(prize.id)">
							<svg width="24" height="24"><use href="#answer_right"></use></svg>
							<img width="140" height="140" :src="prize.image" alt="изображение" />
							<div class="modal-prizes__content">
								<h5 class="modal-prizes__title">{{ prize.title }}</h5>
								<p class="modal-prizes__subtitle" v-if="prize.format === 'ticket'">
									— подойдёт участникам, которые смогут посетить музей в Москве, Калининграде, Самаре или Владивостоке;
								</p>
							</div>
						</button>
						<button class="btn --small --blue" v-if="modalObj.prize.length" @click="storeRequests.setPrize(modalObj.prizeId, idPrize)">Подтвердить</button>
					</div>
					<h5 v-else class="modal-title --center" style="text-align: center">Приз выбран</h5>					
				</div>

				<!-- Акция закончилась -->
				<div class="modal-content" v-if="modalId === 'successSetPrize'">
					<h5 class="modal-title --center" style="text-align: center">Приз выбран!</h5>
				</div>

				<!-- Призы user -->
				<div class="modal-content" v-if="modalId === 'userPrizes'">
					<div class="modal-prizes" v-html="modalObj.greetingText"></div>
				</div>

				<!-- Информация о получении -->
				<div class="modal-content" v-if="modalId === 'downloadReceiptsEnd'">
					<h5 class="modal-title --center">{{ userReceiptsStatus(storeRequests.userData.receipt_status) }}</h5>
				</div>
			</div>
		</Transition>
	</div>
</template>
<style lang="scss" scoped>
@use "@/assets/scss/base/vars";

.v-enter-active,
.v-leave-active {
	transition: 0.5s ease;
}

.v-enter-from {
	opacity: 0;
	transform: translateY(-10%);
}
.v-leave-to {
	opacity: 0;
	transform: translateY(-10%);
}

.modal {
	--transition-time: 0.3s;
	position: fixed;
	left: 0;
	top: 0;
	right: 0;
	bottom: 0;
	z-index: 1000;
	background-color: rgba(0, 0, 0, 0.5);
	backdrop-filter: blur(10.800000190734863px);
	cursor: pointer;
	overflow-y: auto;
	overflow-x: hidden;
	text-align: center;
	opacity: 0;
	visibility: hidden;
	transition: opacity var(--transition-time), visibility var(--transition-time);
}

.modal::before {
	content: "";
	display: inline-block;
	vertical-align: middle;
	height: 100%;
}

.modal__container {
	position: relative;
	width: 665px;
	margin: 50px auto;
	display: none;
	vertical-align: middle;
	background: var(--fill-inverse-1);
	border-radius: 3.2rem;
	cursor: default;
	text-align: left;
	padding: 0;
	@media (max-width: vars.$m) {
		width: 95%;
	}
	@media (max-width: vars.$x480) {
		border-radius: 2.2rem;
	}

	&.--big {
		width: 710px;
		@media (max-width: vars.$m) {
			width: 95%;
		}
	}
}

.modal.is-open {
	opacity: 1;
	visibility: visible;
	transition: opacity var(--transition-time), visibility var(--transition-time);
	.modal__container {
		display: inline-block;
	}
}

.modal__container.modal-open {
	display: inline-block;
}

.fade {
	opacity: 0;
	transition: opacity var(--transition-time);
}

.fade.animate-open {
	opacity: 1;
	transition: opacity var(--transition-time);
}

.fadeInUp {
	opacity: 0;
	transform: translateY(-100px);
	transition: opacity var(--transition-time), transform var(--transition-time);
}

.fadeInUp.animate-open {
	opacity: 1;
	transform: translateY(0);
	transition: opacity var(--transition-time), transform var(--transition-time);
}

.disable-scroll {
	position: relative;
	overflow: hidden;
	height: 100vh;
	position: fixed;
	left: 0;
	top: 0;
	width: 100%;
}

.modal-close {
	position: absolute;
	width: 40px;
	height: 40px;
	top: 30px;
	right: 23px;
	@media (max-width: vars.$x480) {
		top: 10px;
        right: 10px;
		width: 30px;
    	height: 30px;
	}
	svg {
		width: 100%;
		height: 100%;
		stroke-width: 2px;
		stroke: var(--text-accent-1-100);
		transition: transform var(--transition) ease-in;
	}
	@media (hover: hover) {
		&:hover {
			svg {
				transform: scale(1.1);
			}
		}
	}
}

.modal-content {
	display: grid;
	gap: 60px;
	padding: 62px;
	@media (max-width: vars.$x480) {
		padding: 35px;
		gap: 25px;
	}
}

.modal-title {
	font-weight: 500;
	font-size: 32px;
	line-height: 120%;
	color: var(--text-accent-1-100);
	@media (max-width: vars.$xx) {
		font-size: 26px;
	}
	@media (max-width: vars.$x393) {
		font-size: 24px;
	}
	&.--center {
		text-align: center;
	}
}
.modal-subtitle {
	font-family: var(--second-family);
	font-weight: 400;
	font-size: 24px;
	line-height: 140%;
	@media (max-width: vars.$xx) {
		font-size: 16px;
	}
	a {
		color: inherit;
		@media (hover: hover) {
			&:hover {
				text-decoration: underline;
			}
		}
	}
	&.--center {
		text-align: center;
	}
}
.buttons {
	display: grid;
	gap: 56px;
}

.modal-content__desc {
	font-weight: 400;
	font-size: 2.4rem;
	line-height: 140%;
	color: var(--fill-default-1);
	@media (max-width: vars.$x480) {
		font-size: 1.8rem;
	}
	ul {
		padding-left: 3rem;
		list-style-type: disc;
		@media (max-width: vars.$x480) {
			padding-left: 1.5rem;
		}
		li {
		}
	}
}
.modal-content__desc-title {
	font-weight: 400;
	font-size: 2.4rem;
	line-height: 140%;
	margin-bottom: 4rem;
	@media (max-width: vars.$x480) {
		font-size: 1.8rem;
		margin-bottom: 1rem;
	}
}

.modal-prizes {
	display: grid;
	gap: 15px;
	img {
		flex-shrink: 0;
	}
	.btn {
		font-size: 1.6rem;
		padding: 1.8rem 4rem;
		@media (max-width: vars.$x480) {
			    padding: 1.4rem 4rem;
		}
	}
}
.modal-prizes__btn {
	position: relative;
	display: flex;
	align-items: center;
	gap: 20px;
	border: 1px solid var(--border-inverse-3);
	border-radius: var(--border-radius);
	padding: 20px 10px;
	color: var(--text-accent-1-100);	
	@media (max-width: vars.$x480) {
		flex-direction: column;
		text-align: start;
	}
	@media (hover: hover){
		&:hover {
			background: var(--fill-accent-1-3);
		}
	}
	&.active {
		color: var(--text-inverse-100);
		background: var(--fill-accent-1-1);
		border-color: var(--fill-accent-1-1);
		svg {
			display: block;
		}
		&.pointer-events-none {
			pointer-events: auto;
		}
	}
	&.pointer-events-none {
		pointer-events: none;
	}
	svg {
		display: none;
		position: absolute;
		top: 24px;
		right: 24px;
		width: 2.4rem;
		height: 2.4rem;
		stroke: transparent;
	}
}
.modal-prizes__content {
}
.modal-prizes__title {
	font-weight: 500;
	font-size: 2rem;
	line-height: 120%;
	margin-bottom: 20px;
	text-align: start;
}
.modal-prizes__subtitle {
	font-weight: 400;
	font-size: 1.4rem;
	line-height: 140%;
}
</style>
